import { useEffect, useMemo, useState } from 'react'
import { useForecastStore } from '../store/forecastStore'
import type { FinalResult, ForecastRun } from '../types'
import RunMetadataPanel from './RunMetadataPanel'
import { marked } from '../utils/marked'
import { linkifyPlainUrls, stripUnhelpfulCitations, urlHostname } from '../utils/text'
import './SupervisorPanel.css'

interface SupervisorPanelProps {
  finalResult?: FinalResult
  run?: ForecastRun | null
}

export default function SupervisorPanel({ finalResult, run }: SupervisorPanelProps) {
  // Default to hidden; trace is optional once the run is complete.
  const [showTrace, setShowTrace] = useState(false)
  const [showMetadata, setShowMetadata] = useState(false)
  const [showAllSupervisorSources, setShowAllSupervisorSources] = useState(false)
  const [expandedSearches, setExpandedSearches] = useState<Set<number>>(() => new Set())
  const supervisorReasoning = useForecastStore((s) => s.supervisorReasoning)
  const supervisorSearches = useForecastStore((s) => s.supervisorSearches)
  const supervisorVisitedDomains = useForecastStore((s) => s.supervisorVisitedDomains)
  const supervisorStageStarted = useForecastStore((s) => s.supervisorStageStarted)
  const supervisorStageComplete = useForecastStore((s) => s.supervisorStageComplete)

  const traceText = useMemo(() => {
    return (supervisorReasoning || '').trim()
  }, [supervisorReasoning])

  const finalMarkdown = useMemo(() => {
    const text = (finalResult?.full_reasoning || finalResult?.reasoning || '').trim()
    return linkifyPlainUrls(stripUnhelpfulCitations(text))
  }, [finalResult?.full_reasoning, finalResult?.reasoning])

  // Once the final synthesis is available, default to showing it (trace becomes optional)
  useEffect(() => {
    if (finalMarkdown) setShowTrace(false)
  }, [finalMarkdown])

  // While supervisor is actively running and there is no final synthesis yet, show the trace by default.
  useEffect(() => {
    if (!finalMarkdown && supervisorStageStarted) setShowTrace(true)
  }, [finalMarkdown, supervisorStageStarted])

  // No outer collapse: keep this section always visible.
  // (We keep trace toggle only.)

  const finalOutputMarkdown = useMemo(() => {
    if (!finalMarkdown) return ''
    return `## Full reasoning\n\n${finalMarkdown}`.trim()
  }, [finalMarkdown])

  return (
    <section className="supervisor-panel">
      <div className="supervisor-body supervisor-body-prominent" role="region" aria-label="Supervisor output">
        {finalOutputMarkdown ? (
          <div className="supervisor-final">
            <div
              className="supervisor-final-markdown"
              dangerouslySetInnerHTML={{
                __html: marked.parse(finalOutputMarkdown),
              }}
            />
          </div>
        ) : (
          <div className="supervisor-empty">
            Supervisor output will appear here once synthesis completes.
          </div>
        )}

        <div className="supervisor-trace-controls">
          <button
            type="button"
            className="supervisor-trace-toggle"
            onClick={() => setShowTrace((v) => !v)}
            aria-expanded={showTrace}
          >
            {showTrace ? 'Hide AI reasoning' : 'Show AI reasoning'}
          </button>
        </div>

        {(supervisorSearches.length > 0 || supervisorVisitedDomains.length > 0) && (
          <div className="mt-3 rounded-delphi border border-rule bg-paper p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold text-ink">Supervisor sources</div>
              <div className="text-[11px] text-muted">
                {supervisorSearches.length ? `${supervisorSearches.length} searches` : ''}
                {supervisorSearches.length && supervisorVisitedDomains.length ? ' • ' : ''}
                {supervisorVisitedDomains.length ? `${supervisorVisitedDomains.length} domains` : ''}
              </div>
            </div>

            {supervisorVisitedDomains.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {supervisorVisitedDomains.slice(0, 18).map((d) => (
                  <a
                    key={d}
                    className="inline-flex items-center rounded-full border border-rule bg-paper2 px-2 py-0.5 text-[11px] text-muted"
                    title={d}
                    href={`https://${d}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {d}
                  </a>
                ))}
                {supervisorVisitedDomains.length > 18 && (
                  <span className="text-[11px] text-muted">+{supervisorVisitedDomains.length - 18} more</span>
                )}
              </div>
            )}

            {supervisorSearches.length > 0 && (
              <div className="mt-3 space-y-2">
                {(showAllSupervisorSources ? supervisorSearches : supervisorSearches.slice(-4)).map((s, idx) => (
                  <div key={idx} className="text-xs text-muted">
                    <div className="truncate">Searching: {s.query}</div>
                    <div className="mt-1 space-y-1">
                      {(() => {
                        const results = s.results || []
                        const expanded = expandedSearches.has(idx)
                        return (expanded ? results : results.slice(0, 3)).map((r: any, rIdx: number) => (
                          <div key={rIdx} className="truncate">
                            <a
                              className="text-ink hover:underline"
                              href={r.url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {(!r.title || r.title === 'Unknown') && r.url ? urlHostname(r.url) : (r.title || r.url || 'Source')}
                            </a>
                          </div>
                        ))
                      })()}
                      {(s.results || []).length > 3 && (
                        <div className="truncate">
                          <button
                            type="button"
                            className="text-muted hover:text-ink transition underline-offset-2 hover:underline"
                            onClick={() =>
                              setExpandedSearches((prev) => {
                                const next = new Set(prev)
                                if (next.has(idx)) next.delete(idx)
                                else next.add(idx)
                                return next
                              })
                            }
                          >
                            {expandedSearches.has(idx) ? 'Show fewer' : `+${(s.results || []).length - 3} more`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {supervisorSearches.length > 4 && (
              <button
                type="button"
                className="mt-3 text-[11px] text-muted hover:text-ink transition underline-offset-2 hover:underline"
                onClick={() => setShowAllSupervisorSources((v) => !v)}
              >
                {showAllSupervisorSources ? 'Show fewer searches' : `Show all searches (${supervisorSearches.length})`}
              </button>
            )}
          </div>
        )}

        {showTrace && (
          <>
            {traceText ? (
              <pre className="supervisor-text">{traceText}</pre>
            ) : (
              <div className="supervisor-empty">Waiting for supervisor output…</div>
            )}
          </>
        )}

        {(supervisorStageComplete || run?.status === 'complete') && run && (
          <div className="supervisor-metadata-section">
            <button
              type="button"
              className="supervisor-metadata-toggle"
              onClick={() => setShowMetadata((v) => !v)}
              aria-expanded={showMetadata}
            >
              <span className="supervisor-metadata-label">Run metadata</span>
              <span className="supervisor-metadata-icon">{showMetadata ? '▾' : '▸'}</span>
            </button>
            {showMetadata && <RunMetadataPanel run={run} />}
          </div>
        )}
      </div>
    </section>
  )
}


